<template>
    <div
        class="group/card flex flex-row gap-4 rounded bg-white p-4 hover:bg-grey-10">
        <div class="mt-0.5 shrink-0 text-black" v-if="order">
            {{ order }}
        </div>
        <div class="flex w-full flex-col gap-2">
            <div class="flex cursor-pointer items-center justify-between gap-2">
                <div class="flex flex-wrap items-center gap-2">
                    <div class="grow">
                        <slot name="preTitle"></slot>
                        <span v-if="showTag" class="th-badge th-badge-lg mr-2 align-bottom inline-flex items-center px-2 gap-2" :class="generateSourceTag(solution.SOLUTION_TYPE, solution.SOLUTION_SUB_TYPE)">
                            <!-- 显示项目文件类型图标 -->
                            <PtIcon
                                v-if="shouldShowProjectIcon"
                                :icon="getProjectIconName(solution.SOLUTION_SUB_TYPE)"
                                class="text-base"
                            />
                            {{ solutionTagName }}
                        </span>
                        <span
                            @click="handleTitleClick"
                            class="group-hover/card:text-eureka-500 cursor-pointer text-base font-semibold text-black"
                            :title="getContentInnerText(showTitle)" v-safe-html="showTitle"></span>
                        <a class="ml-1 rounded bg-grey-20 px-2 text-xs text-grey-450 h-6 items-center inline-flex"
                           target="_blank" v-if="solution.DOI" :href="doiHref">
                            {{ solution.DOI }}
                        </a>
                        <a class="ml-1 rounded bg-grey-20 px-2 text-xs text-grey-450 h-6 items-center inline-flex"
                           target="_blank" v-if="solution.PN" :href="solutionLink">
                            {{ solution.PN }}
                        </a>
                        <LegalStatus :legal_status="solution.SIMPLE_LEGAL_STATUS" class="ml-2" />
                    </div>

                </div>
                <slot name="customActions" class="shrink-0"></slot>
            </div>

            <div v-if="getContentInnerText(solution.CONTENT)" class="line-clamp-3 text-xs leading-5 text-grey-450"
                 :title="getContentInnerText(solution.CONTENT)">
                <div v-if="showSolutionAiTag"
                     class="solution-card-content__tag inline-block text-xs py-[2px] px-2 mr-2">
                    {{ t('solutionCard.ai_summary') }}
                </div>
                <span v-safe-html="solution.CONTENT"></span>
            </div>

            <slot name="customContent"></slot>

            <ImageRows v-if="solution.PDF_IMAGE_COUNT" :solution-profile="solution" />

            <SolutionCardFooter :show-label="showLabel" :footerField="footerField" :profile="solutionStandardOrgInfo" />
        </div>
    </div>
</template>

<script setup lang="ts">
import { LegalStatus } from '@eureka/shared/legal-status';
import { getContentInnerText } from '@eureka/utils/get-content-inner-text';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import ImageRows from './ImageRows.vue';
import { cn, en, jp, tw } from './locales';
import SolutionCardFooter from './SolutionCardFooter.vue';
import { EContentType, SOLUTION_TYPE, SOLUTION_TYPE_LABEL, type ISolution, EProjectSubType } from './types';
import { getSolutionLink, normalizeSolutionOrgInfo } from './utils';
import { BiTrackService } from '@eureka/domains/src/bi-track/services/bi-track-service';
import { EurekaTrackEventType } from '@eureka/domains/src/bi-track/types';
import { injectService } from '@patsnap/tahiti-core';
import { PtIcon } from 'patsnap-biz';

// 导入项目文件类型图标
import ProjectPdfSvg from './icons/project-pdf.svg';
import ProjectWordSvg from './icons/project-word.svg';
import ProjectExcelSvg from './icons/project-excel.svg';

const biTrackService = injectService<BiTrackService>(BiTrackService);

const { t } = useI18n({
    messages: {
        cn, en, jp, tw,
    },
})
const props = withDefaults(defineProps<{
    solution: ISolution
    order?: number
    footerField?: string
    showTag?: boolean,
    showLabel?: boolean,
    trackData?: Record<string, string>
}>(), {
    showTag: true,
    showLabel: true,
});

const showTitle = computed(() => {
    return props.solution.TITLE || t('solutionCard.noTitle')
})

const showSolutionAiTag = computed(() => {
    return props.solution.CONTENT_TYPE === EContentType.TECHNICAL_TITLE
})

const doiHref = computed(() => {
    return `https://doi.org/${props.solution.DOI}`
})

const solutionLink = computed(() => {
    return getSolutionLink(props.solution, props.trackData)
})

const solutionStandardOrgInfo = computed(() => {
    return normalizeSolutionOrgInfo(props.solution)
})

const solutionTagName = computed(() => {
    if (props.solution.SOLUTION_TYPE === SOLUTION_TYPE.PROJECT) {
        return t(`solutionCard.label.${props.solution.SOLUTION_SUB_TYPE}`)
    }
    return t(`solutionCard.label.${props.solution.SOLUTION_TYPE}`)
})

function generateSourceTag(type: SOLUTION_TYPE, _subType?: EProjectSubType) {
    switch (type) {
    case SOLUTION_TYPE.PATENT:
        return SOLUTION_TYPE_LABEL.PATENT
    case SOLUTION_TYPE.NEWS:
        return SOLUTION_TYPE_LABEL.NEWS
    case SOLUTION_TYPE.PAPER:
        return SOLUTION_TYPE_LABEL.PAPER
    case SOLUTION_TYPE.WEBSITE:
        return SOLUTION_TYPE_LABEL.WEBSITE
    case SOLUTION_TYPE.REPORT:
        return SOLUTION_TYPE_LABEL.REPORT
    case SOLUTION_TYPE.PROJECT:
        // 所有 PROJECT 子类型都使用统一的灰色样式
        return SOLUTION_TYPE_LABEL.PROJECT
    default:
        return SOLUTION_TYPE_LABEL.PAPER
    }
}

// 判断是否需要显示项目文件类型图标
const shouldShowProjectIcon = computed(() => {
    return props.solution.SOLUTION_TYPE === SOLUTION_TYPE.PROJECT &&
           [EProjectSubType.PROJECT_PDF, EProjectSubType.PROJECT_WORD, EProjectSubType.PROJECT_EXCEL].includes(props.solution.SOLUTION_SUB_TYPE as EProjectSubType)
})

// 获取项目文件类型图标组件
function getProjectIconName(subType?: EProjectSubType) {
    switch (subType) {
    case EProjectSubType.PROJECT_PDF:
        return ProjectPdfSvg
    case EProjectSubType.PROJECT_WORD:
        return ProjectWordSvg
    case EProjectSubType.PROJECT_EXCEL:
        return ProjectExcelSvg
    default:
        return null
    }
}

function handleTitleClick() {
    window.open(solutionLink.value, '_blank')

    const { from, otherParam, eventType, eventDetailStr } = props.trackData || {}

    // 解析 eventDetailStr，如果是 JSON 字符串则解析，否则使用空对象
    let parsedEventDetail = {}
    if (eventDetailStr) {
        try {
            parsedEventDetail = JSON.parse(eventDetailStr)
        } catch (e) {
            console.warn('[SolutionCard] Failed to parse eventDetailStr:', e)
        }
    }

    biTrackService.biTrack(eventType || EurekaTrackEventType.AGENT_VIEW, {
        sourceType: from || 'solution-card-title',
        otherParam,
        eventDetailStr: JSON.stringify({
            solution_id: props.solution.SOLUTION_ID,
            solution_type: props.solution.SOLUTION_TYPE,
            solution_sub_type: props.solution.SOLUTION_SUB_TYPE,
            project_id: props.solution.PROJECT_ID,
            project_name: props.solution.PROJECT_NAME,
            ...parsedEventDetail,
        }),
    })
}
</script>

<style lang="scss" scoped>
.solution-card-content__tag {
    position: relative;
    border-radius: 16px;
    background: linear-gradient(270deg, #18EC81 0%, #0DACBA 28.1%, #0A3DFF 66.22%);
    z-index: 1;
}

.solution-card-content__tag::before {
    content: "";
    position: absolute;
    left: 1px;
    right: 1px;
    top: 1px;
    bottom: 1px;
    border-radius: 16px;
    background-color: white;
    z-index: -1;
}
</style>
